import type { EditorElement } from '@wix/component-protocol/schema';
import { DATA, CONTAINER_TYPE } from '@wix/component-protocol/schema';
import type { CompRef } from '@wix/document-services-types';
import type { TemplateContainer } from '@wix/ambassador-devcenter-v1-component-type-data/types';
import { AbstractInternalContainerOverride } from './AbstractInternalContainerOverride';
import {
  findFirstSimpleContainerDefinition,
  mapContainerLayoutToEditorElementLayout,
} from './utils';
import type { EditorPointer } from '@wix/editor-document-logic-api';

export class TemplateContainerOverride extends AbstractInternalContainerOverride {
  resolve(
    editorPointer: EditorPointer<CompRef>,
    containerOwnerPointer: EditorPointer<CompRef>,
    editorElement: EditorElement,
  ): EditorElement | null {
    const templateDefinition =
      this.containerPlatformService.getManifestDefinition(
        editorPointer,
        containerOwnerPointer,
        'template',
      );
    if (!templateDefinition) {
      return null;
    }
    return this.#apply(templateDefinition, editorElement);
  }

  #apply(
    templateDefinition: TemplateContainer,
    editorElement: EditorElement,
  ): EditorElement {
    const simpleEntry = findFirstSimpleContainerDefinition(editorElement);
    if (!simpleEntry) {
      return editorElement;
    }
    const [key, entry] = simpleEntry;
    if (!entry?.[DATA.DATA_TYPE.container]) {
      return editorElement;
    }

    let updated: EditorElement = {
      ...editorElement,
      data: {
        ...editorElement.data,
        [key]: {
          ...entry,
          [DATA.DATA_TYPE.container]: {
            ...entry[DATA.DATA_TYPE.container],
            containerType: CONTAINER_TYPE.template,
            template: templateDefinition,
          },
        },
      },
    };

    if (templateDefinition.layout) {
      updated = {
        ...updated,
        layout: {
          ...(updated.layout ?? {}),
          ...(this.containerPlatformService.experimentsAPI.enabled(
            'dmSystemContainerMinWidth',
          )
            ? mapContainerLayoutToEditorElementLayout(templateDefinition.layout)
            : templateDefinition.layout),
        },
      };
    }

    return updated;
  }
}
